import { useQuery } from '@tanstack/react-query';

import { useCapability } from '../../../hooks/use-capability';
import { api } from '../../../lib/api-client';

export const PLAYBOOK_NAME_MAP_QUERY_KEY = ['playbooks', 'active', 'name-map'] as const;

/**
 * usePlaybookNameMap — S7 PR3 (S6 Condução Governada, Q-J6/Q-J8).
 *
 * Resolução HONESTA de nomes de playbook para exibição (modal de
 * confirmar/rejeitar transição + picker de troca manual). Usa
 * `GET /v1/playbooks?status=active` (cap `playbook-read`, JÁ na base do
 * atendente comum) — deliberadamente NÃO `listAvailablePlaybooks`
 * (`/v1/supervisor-actions/.../available-playbooks`), que exige a cap
 * `force-playbook-switch` (supervisor-only) e por isso é inalcançável pelo
 * atendente comum dono de `playbook-manual-switch`/`conversation-lifecycle-strict`
 * (R15 confirmado em `capabilities.spec.yaml`).
 *
 * Um id ausente do mapa (playbook inativo/removido) cai no fallback honesto
 * do caller — mostrar o próprio ID, NUNCA um nome fabricado.
 */
export function usePlaybookNameMap(enabled: boolean): {
  nameMap: Map<string, string>;
  playbooks: { id: string; name: string }[];
  isLoading: boolean;
} {
  // Nit Reviewer A (S7 PR3): `GET /v1/playbooks` exige `playbook-read` (Team+);
  // gatear o fetch nele evita o 403 → toast global "Sem acesso" num tenant Solo
  // onde uma proposta apareça. Sem a cap, o nome cai no fallback honesto (o ID).
  const canReadPlaybooks = useCapability('playbook-read');
  const q = useQuery({
    queryKey: PLAYBOOK_NAME_MAP_QUERY_KEY,
    queryFn: () => api.listPlaybooks({ status: 'active', limit: 200 }),
    enabled: enabled && canReadPlaybooks,
    staleTime: 60_000,
  });

  const playbooks = (q.data ?? []).map((p) => ({ id: p.id, name: p.name }));
  const nameMap = new Map(playbooks.map((p) => [p.id, p.name]));

  return { nameMap, playbooks, isLoading: q.isLoading };
}

/** Fallback honesto: nome resolvido do mapa, ou o próprio ID quando ausente. */
export function resolvePlaybookName(id: string | null, nameMap: Map<string, string>): string {
  if (id === null) return '(sem playbook anterior)';
  return nameMap.get(id) ?? id;
}
